import {
  Button,
  type Key,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Select,
  SelectValue,
} from 'react-aria-components'

import ChevronDownSvg from '@/assets/icons/chevron-down.svg?react'
import australiaSvg from '@/assets/icons/flags/australia.svg'
import canadaSvg from '@/assets/icons/flags/canada.svg'
import unitedKingdomSvg from '@/assets/icons/flags/united-kingdom.svg'
import unitedStatesSvg from '@/assets/icons/flags/united-states.svg'
import useCurrentVertical, { Vertical } from '@/hooks/useCurrentVertical'
import {FOOD_SUPPORTED_COUNTRIES as FOOD_GRUBHUB_SUPPORTED_COUNTRIES} from '@/lib/food-grubhub/config'
import {
  isSupportedCountry,
  SUPPORTED_COUNTRIES,
  type SupportedCountries,
  useCountry,
} from '@/lib/locale/country'
import {setUserSelectedLocale} from '@/lib/locale/locale'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'

import style from './CountrySelector.module.scss'

const COUNTRY_LABELS = {
  US: 'United States',
  GB: 'United Kingdom',
  AU: 'Australia',
  CA: 'Canada',
} as const satisfies Record<SupportedCountries, string>

const COUNTRY_ICONS = {
  US: unitedStatesSvg,
  GB: unitedKingdomSvg,
  AU: australiaSvg,
  CA: canadaSvg,
} as const satisfies Record<SupportedCountries, string>

interface Props {
  variant?: 'light' | 'dark'
}

export default memo(function CountrySelector({variant}: Props) {
  const [country, setCountry] = useCountry()
  const currentVertical = useCurrentVertical()

  const supportedCountries = useMemo(() => {
    switch (currentVertical) {
      case Vertical.FoodGrubHub:
        return FOOD_GRUBHUB_SUPPORTED_COUNTRIES
      case Vertical.Shop:
      case Vertical.Flight:
      case undefined:
        return SUPPORTED_COUNTRIES
    }
  }, [currentVertical])

  const onSelectionChange = useCallback(
    (q: Key) => {
      if (isSupportedCountry(q)) {
        setCountry(q)
        setUserSelectedLocale()
      }
    },
    [setCountry],
  )

  return (
    <Select className={style.Select} selectedKey={country} onSelectionChange={onSelectionChange}>
      <VisuallyHidden as={Label}>Country: {COUNTRY_LABELS[country]}</VisuallyHidden>
      <Button
        className={clsx(
          style.SelectButton,
          variant === 'dark' && style.Dark,
          variant === 'light' && style.Light,
        )}
      >
        <SelectValue className={style.Value} />
        <span aria-hidden className={style.Chevron}>
          <ChevronDownSvg
            className={style.ChevronIcon}
            width={12}
            height={16}
            viewBox='0 0 24 24'
            aria-hidden
          />
        </span>
      </Button>
      <Popover className={style.SelectList}>
        <ListBox className={style.ListBox}>
          {supportedCountries.map(value => (
            <ListBoxItem className={style.ListBoxItem} key={value} id={value} textValue={value}>
              <img src={COUNTRY_ICONS[value]} alt='' width={32} height={24} />
              <span className={style.Label}>{COUNTRY_LABELS[value]}</span>
            </ListBoxItem>
          ))}
        </ListBox>
      </Popover>
    </Select>
  )
})
